import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate, formatApiError } from "@/lib/api";
import SkeletonPage, { SkeletonCard, SkeletonTable } from "@/components/KovaSkeleton";
import {
  ArrowLeft, Phone, Mail, MapPin, Receipt, Wrench, BellRing, FileText, MessageSquareText,
  Smartphone, MessageSquare, CheckCircle2, XCircle, MinusCircle, BellOff, BellRing as BellRingOn,
  User, Building2, Landmark, AlertTriangle, Pencil, History, Package, ShieldCheck,
  Calendar, ClipboardList,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const TYPE_META = {
  individual: { label: "Individual / Farmer", icon: User, colour: "#166534", badge: "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/25", dot: "bg-green-500" },
  business: { label: "Business / Organization", icon: Building2, colour: "#D97706", badge: "bg-yellow-500/15 text-yellow-200 border-yellow-500/30", dot: "bg-amber-500" },
  government: { label: "Government / Agency", icon: Landmark, colour: "#991b1b", badge: "bg-red-500/15 text-red-300 border-red-500/25", dot: "bg-red-500" },
};

const STATUS_META = {
  active: { label: "Active", cls: "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/25" },
  inactive: { label: "Inactive", cls: "bg-[#1A1F1C] text-[#F8F9FA] border-[#3A4640]" },
  archived: { label: "Archived", cls: "bg-red-500/15 text-red-300 border-red-500/25" },
};

const MODULE_META = {
  customer: { label: "Customer", colour: "#166534", icon: User },
  sales: { label: "Sales", colour: "#166534", icon: Receipt },
  service: { label: "Service", colour: "#0f766e", colourText: "text-teal-700", icon: Wrench },
  followup: { label: "Follow-up", colour: "#D97706", icon: BellRing },
  marketing: { label: "Marketing", colour: "#D97706", icon: MessageSquareText },
  engagement: { label: "Engagement", colour: "#7c3aed", icon: MessageSquareText },
  accounts: { label: "Accounts", colour: "#991b1b", icon: FileText },
};

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [subsidies, setSubsidies] = useState([]);
  const [machines, setMachines] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [historicalEquip, setHistoricalEquip] = useState([]);
  const [timelineModule, setTimelineModule] = useState("all");
  const [tab, setTab] = useState("details");
  const [optOutOpen, setOptOutOpen] = useState(false);
  const [optOutReason, setOptOutReason] = useState("");

  const loadAll = async () => {
    const [p, m, t, s, mo, ic, he] = await Promise.all([
      api.get(`/customers/${id}/profile`),
      api.get(`/customers/${id}/messages`).catch(() => ({ data: [] })),
      api.get(`/customers/${id}/timeline`).catch(() => ({ data: [] })),
      api.get(`/customers/${id}/subsidies`).catch(() => ({ data: [] })),
      api.get(`/customers/${id}/machines`).catch(() => ({ data: [] })),
      api.get(`/customers/${id}/interactions`).catch(() => ({ data: [] })),
      api.get(`/customers/${id}/historical-equipment`).catch(() => ({ data: [] })),
    ]);
    setData(p.data);
    setMessages(m.data || []);
    setTimeline(t.data || []);
    setSubsidies(s.data || []);
    setMachines(mo.data || []);
    setInteractions(ic.data || []);
    setHistoricalEquip(he.data || []);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);

  const submitOptOut = async () => {
    try {
      await api.post(`/customers/${id}/opt-out`, { reason: optOutReason });
      toast.success("Customer opted out — future broadcasts will skip them");
      setOptOutOpen(false); setOptOutReason("");
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const optIn = async () => {
    try {
      await api.post(`/customers/${id}/opt-in`);
      toast.success("Customer opted back in");
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const filteredTimeline = useMemo(() => {
    if (timelineModule === "all") return timeline;
    return timeline.filter((t) => t.module === timelineModule);
  }, [timeline, timelineModule]);

  if (!data) return <SkeletonPage title kpi={false} table rows={4} cols={4} />;

  const { customer, sales, services, followups, stats } = data;
  const type = TYPE_META[customer.customer_type] || TYPE_META.individual;
  const status = STATUS_META[customer.status] || STATUS_META.active;
  const displayName = customer.customer_type === "business"
    ? (customer.organization_name || customer.name)
    : customer.customer_type === "government"
      ? (customer.department_name || customer.name)
      : customer.name;
  const subtitle = customer.customer_type === "business"
    ? `${customer.contact_person || ""}${customer.designation ? " · " + customer.designation : ""}`
    : customer.customer_type === "government"
      ? `${customer.office_division || ""}${customer.contact_person ? " · " + customer.contact_person : ""}`
      : (customer.father_or_husband_name ? `S/o ${customer.father_or_husband_name}` : "");
  const TypeIcon = type.icon;
  const addressLine = [customer.village, customer.gram_panchayat, customer.post_office, customer.block, customer.district, customer.state, customer.pin_code].filter(Boolean).join(", ");
  const missingLabels = (customer.missing_required || []).map((k) => ({
    name: "Customer Name", father_or_husband_name: "Father's / Husband's Name",
    organization_name: "Organization Name", department_name: "Department Name",
    office_division: "Office / Division Name", contact_person: "Contact Person",
    designation: "Designation", phone: "Mobile Number", email: "Email ID",
    village: "Village / Locality", post_office: "Post Office", block: "Block / Tehsil",
    district: "District", state: "State", pin_code: "PIN Code",
  }[k] || k));

  return (
    <div className="p-8 lg:p-10 max-w-[1500px]">
      <Link to="/customers" className="inline-flex items-center gap-2 text-sm text-[#8A8F98] hover:text-[#F8F9FA] mb-4" data-testid="back-to-customers">
        <ArrowLeft size={16} /> Back to Customers
      </Link>

      {customer.incomplete && (
        <div className="mb-4 bg-yellow-500/10 border border-yellow-500/40 rounded-md p-4 flex flex-wrap items-start gap-3" data-testid="incomplete-banner">
          <AlertTriangle size={20} className="text-yellow-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-[250px]">
            <div className="font-heading font-bold text-yellow-200">This customer profile is incomplete.</div>
            <div className="text-xs text-yellow-300 mt-1">
              Missing required fields: <span className="font-medium">{missingLabels.join(" · ")}</span>
            </div>
          </div>
          <Button
            onClick={() => navigate(`/customers?edit=${customer.id}`)}
            data-testid="complete-profile-btn"
            className="bg-amber-700 hover:bg-amber-800 text-white"
          >
            <Pencil size={14} className="mr-2" /> Complete Profile
          </Button>
        </div>
      )}

      {/* Header card */}
      <div className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm p-6 lg:p-8 mb-6" data-testid="customer-profile-header">
        <div className="flex flex-wrap items-start gap-5">
          <div className="w-16 h-16 rounded-md flex items-center justify-center shrink-0" style={{ background: type.colour }}>
            <TypeIcon size={32} className="text-white" />
          </div>
          <div className="flex-1 min-w-[300px]">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1.5 text-xs font-heading px-2 py-1 rounded border ${type.badge}`} data-testid="profile-type-badge">
                <span className={`w-2 h-2 rounded-full ${type.dot}`}></span> {type.label}
              </span>
              <span className={`text-xs font-heading px-2 py-1 rounded border ${status.cls}`} data-testid="profile-status-badge">{status.label}</span>
              {customer.opted_out && (
                <span className="inline-flex items-center gap-1 text-xs bg-yellow-500/10 border border-yellow-500/40 text-yellow-200 px-2 py-1 rounded font-heading" data-testid="profile-optedout-badge">
                  <BellOff size={11} /> Opted out
                </span>
              )}
            </div>
            <div className="font-mono text-xs text-[#8A8F98] mb-1" data-testid="profile-tzat">{customer.tzat_no}</div>
            <h1 className="font-heading text-3xl font-extrabold text-[#F8F9FA] tracking-tight" data-testid="profile-name">{displayName || "(no name)"}</h1>
            {subtitle && <div className="text-sm text-[#8A8F98] mt-1">{subtitle}</div>}
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-[#8A8F98]">
              {customer.phone && <span className="flex items-center gap-1.5"><Phone size={14} />{customer.phone}</span>}
              {customer.email && <span className="flex items-center gap-1.5"><Mail size={14} />{customer.email}</span>}
              {(customer.village || customer.district) && <span className="flex items-center gap-1.5"><MapPin size={14} />{[customer.village, customer.district, customer.state].filter(Boolean).join(", ")}</span>}
              <span className="flex items-center gap-1.5 text-[#8A8F98]"><Calendar size={14} /> Customer since {formatDate(customer.created_at)}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 min-w-[320px]">
            <MetricTile label="Total spent" value={formatCurrency(stats.total_spent)} colour="#0f2417" accent="#D97706" testid="stat-total-spent" />
            <MetricTile label="Outstanding" value={formatCurrency(stats.total_due)} colour="#F5F5F4" text="#DC2626" testid="stat-outstanding" />
            <MetricTile label="Purchases" value={stats.purchases} colour="#F5F5F4" testid="stat-purchases" />
            <MetricTile label="Services" value={stats.services} colour="#F5F5F4" testid="stat-services" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 flex-wrap h-auto" data-testid="profile-tabs">
          <TabsTrigger value="details" data-testid="ptab-details">Customer Details</TabsTrigger>
          <TabsTrigger value="sales" data-testid="ptab-sales">Sales</TabsTrigger>
          <TabsTrigger value="machines" data-testid="ptab-machines">Machines Owned</TabsTrigger>
          <TabsTrigger value="subsidy" data-testid="ptab-subsidy">Subsidy</TabsTrigger>
          <TabsTrigger value="service" data-testid="ptab-service">Service History</TabsTrigger>
          <TabsTrigger value="communication" data-testid="ptab-communication">Communication</TabsTrigger>
          <TabsTrigger value="interactions" data-testid="ptab-interactions">Calls</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="ptab-timeline"><History size={12} className="mr-1.5" /> Timeline</TabsTrigger>
          <TabsTrigger value="future" data-testid="ptab-future">More</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <SectionCard title="Customer Overview" icon={ClipboardList}>
              <KV label="TZAT #" value={customer.tzat_no} mono />
              <KV label="Customer Type" value={type.label} />
              <KV label="Status" value={status.label} />
              <KV label="Customer Since" value={formatDate(customer.created_at)} />
              {customer.farmer_id && <KV label="Farmer ID" value={customer.farmer_id} mono />}
              {customer.gst_number && <KV label="GST" value={customer.gst_number} mono />}
            </SectionCard>

            <SectionCard title="Communication" icon={MessageSquareText}>
              <KV label="Mobile" value={customer.phone || "—"} />
              {customer.alternate_phone && <KV label="Alternate Mobile" value={customer.alternate_phone} />}
              {customer.office_phone && <KV label="Office Phone" value={customer.office_phone} />}
              <KV label="Email" value={customer.email || "—"} />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Preferred channels</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(customer.preferred_communication || []).length === 0 && <span className="text-xs text-[#8A8F98]">Not set</span>}
                  {(customer.preferred_communication || []).map((c) => (
                    <span key={c} className="text-xs bg-[#121815] text-[#F8F9FA] px-2 py-0.5 rounded capitalize">{c}</span>
                  ))}
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Address" icon={MapPin}>
              {addressLine ? <div className="text-sm text-[#F8F9FA]">{addressLine}</div> : <div className="text-sm text-[#8A8F98]">No address recorded</div>}
              {customer.customer_type === "individual" && customer.gram_panchayat && (
                <KV label="Gram Panchayat" value={customer.gram_panchayat} />
              )}
              {customer.pin_code && <KV label="PIN" value={customer.pin_code} mono />}
            </SectionCard>
          </div>

          <SectionCard title="Recent Activity" icon={History}>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
              <RecentTile label="Last purchase" value={sales[0] ? formatDate(sales[0].sale_date) : "—"} sub={sales[0] ? `${sales[0].product?.brand || ""} ${sales[0].product?.model || ""}`.trim() : "No purchase yet"} />
              <RecentTile label="Last service" value={services[0] ? formatDate(services[0].service_date) : "—"} sub={services[0]?.service_type || "No service yet"} />
              <RecentTile label="Last follow-up" value={followups[0] ? formatDate(followups[0].due_date) : "—"} sub={followups[0]?.title || "No follow-ups yet"} />
              <RecentTile label="Last message" value={messages[0] ? formatDate(messages[0].sent_at) : "—"} sub={messages[0] ? `${(messages[0].channel || "").toUpperCase()} · ${messages[0].source}` : "No messages yet"} />
            </div>
          </SectionCard>

          {customer.notes && (
            <SectionCard title="Notes" icon={ClipboardList}>
              <div className="text-sm text-[#F8F9FA] italic whitespace-pre-wrap">&ldquo;{customer.notes}&rdquo;</div>
            </SectionCard>
          )}
        </TabsContent>

        <TabsContent value="sales">
          <SectionCard title={`Purchase history (${sales.length})`} icon={Receipt}>
            {sales.length === 0 ? (
              <p className="text-sm text-[#8A8F98]">No purchases yet.</p>
            ) : (
              <ul className="space-y-3">
                {sales.map((s) => (
                  <li key={s.id} className="border-b border-[#3A4640] last:border-0 pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-[#F8F9FA] text-sm">{s.product?.brand} {s.product?.model}</div>
                        <div className="text-xs text-[#8A8F98] mt-0.5">{formatDate(s.sale_date)} · Qty {s.quantity}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-semibold text-[#39FF14]">{formatCurrency(s.total_amount)}</div>
                        <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#121815]">{s.payment_status}</span>
                      </div>
                    </div>
                    <Link to={`/sales/${s.id}/invoice`} className="inline-flex items-center gap-1 text-xs text-[#8A8F98] hover:text-[#39FF14] mt-2">
                      <FileText size={11} /> View invoice · {s.invoice_no}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="machines">
          <SectionCard title={`Machines & Implements Owned (${machines.length})`} icon={Package}>
            {machines.length === 0 ? (
              <p className="text-sm text-[#8A8F98]">No machines recorded yet. Machines are created automatically when a Sale is invoiced.</p>
            ) : (
              <ul className="space-y-3" data-testid="machines-list">
                {machines.map((m) => {
                  const productLabel = [m.product?.brand, m.product?.model || m.product?.name].filter(Boolean).join(" ").trim();
                  const histLabel = [m.hist_brand, m.hist_model, m.hist_variant].filter(Boolean).join(" ").trim();
                  const title = productLabel || histLabel || m.hist_category || "Machine";
                  const subLabel = !productLabel && m.hist_category && (histLabel || m.hist_category !== title) ? m.hist_category : "";
                  return (
                  <li key={m.id} className="border-b border-[#3A4640] last:border-0 pb-3" data-testid={`machine-${m.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-[#F8F9FA] text-sm">{title}</span>
                          {subLabel && <span className="text-xs text-[#8A8F98]">· {subLabel}</span>}
                          {m.promoted_from_history && (
                            <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-emerald-100 text-[#39FF14] border-emerald-200" data-testid={`machine-promoted-${m.id}`}>
                              Confirmed from history
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[#8A8F98] mt-0.5">
                          {m.purchased_at ? <>Purchased {formatDate(m.purchased_at)}</> : <span className="italic">Purchased (historical, date unknown)</span>}
                          {m.invoice_no && m.sale_id && <> · <Link to={`/sales/${m.sale_id}/invoice`} className="text-[#39FF14] hover:underline">Invoice {m.invoice_no}</Link></>}
                          {m.source_history_batch && <> · Batch <span className="font-mono">{m.source_history_batch}</span></>}
                        </div>
                        {m.subsidy_case_no && (
                          <div className="text-xs text-[#39FF14] font-mono mt-0.5">Subsidy · {m.subsidy_case_no}</div>
                        )}
                      </div>
                      <div className="text-right text-xs grid grid-cols-1 gap-0.5">
                        {m.serial_number && <span className="font-mono text-[#F8F9FA]">S/N {m.serial_number}</span>}
                        {m.chassis_number && <span className="font-mono text-[#F8F9FA]">Chassis {m.chassis_number}</span>}
                        {m.engine_number && <span className="font-mono text-[#F8F9FA]">Engine {m.engine_number}</span>}
                        {m.warranty_expiry && <span className="text-yellow-400">Warranty until {formatDate(m.warranty_expiry)}</span>}
                      </div>
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>
          {historicalEquip.length > 0 && (
            <SectionCard title={`Unverified Historical Records (${historicalEquip.length})`} icon={Package}>
              <div className="text-xs text-yellow-300 bg-yellow-500/10 border border-yellow-500/30 rounded p-2 mb-3 flex items-start gap-2">
                <History size={12} className="mt-0.5 shrink-0" />
                <div>
                  <strong className="font-heading">Historical evidence only — not current ownership.</strong>{" "}
                  These records were imported from historical data and have not yet been verified against the customer's current equipment.
                  Confirm ownership to convert a record into a current machine entry (it will then disappear from this list and appear above under Machines & Implements Owned).
                </div>
              </div>
              <ul className="space-y-3" data-testid="historical-equipment-list">
                {historicalEquip.map((h) => (
                  <li key={h.id} className="border-b border-[#3A4640] last:border-0 pb-3" data-testid={`hist-equip-${h.id}`}>
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-heading font-semibold text-[#F8F9FA]">{h.category || "Equipment"}</span>
                          {(h.brand || h.model) && <span className="text-sm text-[#F8F9FA]">{h.brand} {h.model}</span>}
                          <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-[#121815] text-[#8A8F98] border-[#3A4640]">
                            {h.confidence === "exact_product" ? "Product matched" : h.confidence === "brand_model" ? "Brand + model" : "Category only"}
                          </span>
                          <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border bg-yellow-500/15 text-yellow-200 border-yellow-500/30" data-testid={`hist-unverified-badge-${h.id}`}>
                            Historical · Unverified
                          </span>
                        </div>
                        <div className="text-xs text-[#8A8F98] mt-0.5">
                          {h.historical_date && <>Dated {formatDate(h.historical_date)} · </>}
                          Batch <span className="font-mono">{h.source?.batch_code}</span> · from <span className="italic">{h.source?.workbook} / {h.source?.sheet} · row {h.source?.row}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 shrink-0">
                        <div className="text-right text-xs grid grid-cols-1 gap-0.5">
                          {h.serial_number && <span className="font-mono text-[#F8F9FA]">S/N {h.serial_number}</span>}
                          {h.chassis_number && <span className="font-mono text-[#F8F9FA]">Chassis {h.chassis_number}</span>}
                          {h.engine_number && <span className="font-mono text-[#F8F9FA]">Engine {h.engine_number}</span>}
                          {h.invoice_no && <span className="text-[#F8F9FA]">Invoice {h.invoice_no}</span>}
                        </div>
                        <Button
                          size="sm"
                          className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] text-xs h-7 px-2.5"
                          onClick={async () => {
                            if (!window.confirm(`Confirm the ${h.category || "equipment"} is currently owned by this customer? A machine record will be created and this historical row will be removed from this list.`)) return;
                            try {
                              await api.post(`/customers/${id}/historical-equipment/${h.id}/promote`);
                              toast.success("Ownership confirmed — machine record created");
                              loadAll();
                            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to confirm ownership"); }
                          }}
                          data-testid={`promote-history-btn-${h.id}`}
                        >
                          <CheckCircle2 size={12} className="mr-1" /> Confirm current ownership
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}
        </TabsContent>

        <TabsContent value="subsidy">
          <SectionCard title={`Subsidy cases (${subsidies.length})`} icon={FileText}>
            {subsidies.length === 0 ? (
              <p className="text-sm text-[#8A8F98]">No subsidy cases yet. Create one from the <Link to="/subsidies" className="text-[#39FF14] font-semibold hover:underline">Subsidy Desk</Link>.</p>
            ) : (
              <ul className="space-y-3" data-testid="customer-subsidy-list">
                {subsidies.map((s) => (
                  <li key={s.id} className="border-b border-[#3A4640] last:border-0 pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <Link to={`/subsidies/${s.id}`} className="font-mono text-xs text-[#8A8F98] hover:text-[#39FF14]">{s.case_no}</Link>
                        <div className="font-medium text-[#F8F9FA] text-sm">{s.scheme || "Scheme TBD"}</div>
                        <div className="text-xs text-[#8A8F98] mt-0.5">{s.department}{s.financial_year ? ` · FY ${s.financial_year}` : ""} · {s.approved_machine_category || s.machine_category}</div>
                        {s.permit_number && <div className="text-xs text-[#39FF14] font-mono mt-0.5">Permit {s.permit_number}</div>}
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-heading uppercase tracking-wider text-[#8A8F98] bg-[#121815] px-2 py-1 rounded">{s.status.replace(/_/g, " ")}</div>
                        <div className="text-xs text-[#8A8F98] mt-1">Applied {s.application_date ? formatDate(s.application_date) : "—"}</div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="service">
          <SectionCard title={`Service history (${services.length})`} icon={Wrench}>
            {services.length === 0 ? (
              <p className="text-sm text-[#8A8F98]">No services recorded.</p>
            ) : (
              <ul className="space-y-3">
                {services.map((s) => (
                  <li key={s.id} className="border-b border-[#3A4640] last:border-0 pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-[#F8F9FA] text-sm">{s.service_type} · {s.service_no}</div>
                        <div className="text-xs text-[#8A8F98] mt-0.5">{formatDate(s.service_date)} · {s.product?.brand} {s.product?.model}</div>
                        <div className="text-sm text-[#8A8F98] mt-1">{s.description}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-sm">{formatCurrency(s.cost || 0)}</div>
                        <span className="text-xs font-medium px-2 py-0.5 rounded bg-[#121815]">{s.status}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
          {followups.length > 0 && (
            <SectionCard title={`Follow-ups (${followups.length})`} icon={BellRing}>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {followups.map((f) => (
                  <div key={f.id} className="border border-[#3A4640] rounded-md p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium uppercase text-yellow-400 font-heading">{f.priority}</span>
                      <span className="text-xs bg-[#121815] text-[#F8F9FA] px-2 py-0.5 rounded">{f.status}</span>
                    </div>
                    <div className="font-medium text-[#F8F9FA] text-sm">{f.title}</div>
                    <div className="text-xs text-[#8A8F98] mt-0.5">Due {formatDate(f.due_date)}</div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </TabsContent>

        <TabsContent value="communication">
          <SectionCard
            title={`Messaging history (${messages.length})`}
            icon={MessageSquareText}
            action={customer.opted_out ? (
              <Button variant="outline" size="sm" onClick={optIn} data-testid="customer-opt-in-btn" className="text-[#39FF14] border-[#39FF14]/40 hover:bg-green-50">
                <BellRingOn size={13} className="mr-1.5" /> Opt back in
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setOptOutOpen(true)} data-testid="customer-opt-out-btn" className="text-yellow-300 border-yellow-500/40 hover:bg-yellow-500/10">
                <BellOff size={13} className="mr-1.5" /> Opt out of broadcasts
              </Button>
            )}
          >
            {messages.length === 0 ? (
              <p className="text-sm text-[#8A8F98]">No SMS or WhatsApp exchanged with this customer yet.</p>
            ) : (
              <ul className="space-y-3" data-testid="customer-messages-list">
                {messages.map((m) => {
                  const Icon = m.channel === "whatsapp" ? MessageSquare : Smartphone;
                  const st = m.status === "sent"
                    ? { c: "text-[#39FF14]", i: <CheckCircle2 size={12} />, l: "delivered" }
                    : m.status === "failed"
                      ? { c: "text-red-600", i: <XCircle size={12} />, l: "failed" }
                      : { c: "text-[#8A8F98]", i: <MinusCircle size={12} />, l: m.status || "logged" };
                  const sourceLabel = { service: "Service update", followup: "Follow-up", broadcast: "Broadcast", inbound: "Customer reply" }[m.source] || m.source || "Message";
                  return (
                    <li key={m.id} className="border-b border-[#3A4640] last:border-0 pb-3" data-testid={`message-${m.id}`}>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="inline-flex items-center gap-1 text-[#8A8F98]"><Icon size={12} /> {(m.channel || "").toUpperCase()}</span>
                        <span className="inline-block bg-[#121815] text-[#F8F9FA] px-1.5 py-0.5 rounded font-heading uppercase tracking-wider text-[10px]">{sourceLabel}</span>
                        <span className={`inline-flex items-center gap-1 ${st.c}`}>{st.i} {st.l}</span>
                        <span className="ml-auto text-[#8A8F98] font-mono">{formatDate(m.sent_at)}</span>
                      </div>
                      <div className="text-sm text-[#F8F9FA] mt-1.5 whitespace-pre-wrap">{m.body}</div>
                      {m.error && <div className="text-xs text-red-600 mt-1">{m.error}</div>}
                      {m.sent_by_name && <div className="text-[11px] text-[#8A8F98] mt-1">by {m.sent_by_name}</div>}
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="timeline">
          <SectionCard
            title={`Activity timeline (${filteredTimeline.length})`}
            icon={History}
            action={
              <Select value={timelineModule} onValueChange={setTimelineModule}>
                <SelectTrigger data-testid="timeline-module-filter" className="w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All modules</SelectItem>
                  {Object.entries(MODULE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            }
          >
            {filteredTimeline.length === 0 ? (
              <div className="text-sm text-[#8A8F98]" data-testid="timeline-empty">No activity yet in this range.</div>
            ) : (
              <ol className="relative border-l-2 border-[#3A4640] ml-3 space-y-4" data-testid="customer-timeline-list">
                {filteredTimeline.map((e) => {
                  const m = MODULE_META[e.module] || { label: e.module, colour: "#78716c", icon: History };
                  const MIcon = m.icon;
                  return (
                    <li key={e.id} className="pl-6 relative" data-testid={`timeline-${e.id}`}>
                      <div className="absolute -left-[13px] top-0 w-6 h-6 rounded-full flex items-center justify-center text-white shadow" style={{ background: m.colour }}>
                        <MIcon size={12} />
                      </div>
                      <div className="text-sm text-[#F8F9FA] font-medium">{e.description}</div>
                      <div className="text-xs text-[#8A8F98] mt-0.5">
                        {formatDate(e.occurred_at)} · <span className="uppercase tracking-wider font-heading">{m.label}</span> · {e.event}
                        {e.user_name && <> · by {e.user_name}</>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="interactions">
          <SectionCard title={`Calling / Engagement (${interactions.length})`} icon={MessageSquareText}>
            {interactions.length === 0 ? (
              <p className="text-sm text-[#8A8F98]" data-testid="interactions-empty">No calls recorded yet. Use <Link to="/engagement" className="text-[#39FF14] font-semibold hover:underline">Customer Engagement</Link> to log calls.</p>
            ) : (
              <ul className="space-y-3" data-testid="interactions-list">
                {interactions.map((i) => (
                  <li key={i.id} className="border-b border-[#3A4640] last:border-0 pb-3" data-testid={`interaction-${i.id}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-heading font-semibold text-[#F8F9FA]">{i.outcome_label}</div>
                        <div className="text-xs text-[#8A8F98] mt-0.5">{formatDate(i.called_at)} · by {i.user_name || "—"}</div>
                        {i.note && <div className="text-sm text-[#F8F9FA] mt-1.5 italic">&ldquo;{i.note}&rdquo;</div>}
                        {i.interest_summary && (
                          <div className="text-xs text-[#39FF14] bg-[#39FF14]/5 border border-emerald-200 rounded px-2 py-1 mt-1.5 inline-block">
                            Interest: {i.interest_summary}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        {i.next_action && i.next_action !== "none" && (
                          <span className="text-[10px] uppercase font-heading tracking-wider text-yellow-200 bg-yellow-500/15 border border-yellow-500/30 px-1.5 py-0.5 rounded">
                            → {i.next_action.replace(/_/g, " ")}
                          </span>
                        )}
                        {i.message_sent && (
                          <div className="text-[10px] uppercase font-heading tracking-wider text-[#39FF14] mt-1">Message sent · {i.message_channel}</div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="future">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FutureCard icon={FileText} title="Quotations" hint="Coming soon — draft quotes, revisions and conversion tracking." />
            <FutureCard icon={Package} title="Machines Owned" hint="Automatically compiled from purchase records." live count={stats.purchases} />
            <FutureCard icon={ShieldCheck} title="Warranty" hint="Coming soon — active warranty tracking + claims." />
            <FutureCard icon={ShieldCheck} title="AMC / Annual Maintenance" hint="Coming soon — scheduled visits & renewal alerts." />
            <FutureCard icon={FileText} title="Payments" hint="Coming soon — outstanding, receivables & receipts." live count={sales.filter((s) => (s.balance_due || 0) > 0).length} liveLabel="pending" />
            <FutureCard icon={FileText} title="Reports" hint="Coming soon — customer-specific analytics & exports." />
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={optOutOpen} onOpenChange={setOptOutOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Opt {displayName} out of broadcasts?</DialogTitle>
            <DialogDescription>
              They will no longer receive bulk SMS/WhatsApp broadcasts. Transactional messages (service updates, follow-up reminders) still send.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading">Reason (optional)</Label>
            <Input data-testid="opt-out-reason-input" value={optOutReason} onChange={(e) => setOptOutReason(e.target.value)} placeholder="e.g. requested by phone" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOptOutOpen(false)}>Cancel</Button>
            <Button onClick={submitOptOut} className="bg-amber-700 hover:bg-amber-800 text-white" data-testid="opt-out-confirm-btn">
              <BellOff size={14} className="mr-1.5" /> Opt out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, action }) {
  return (
    <section className="bg-[#121815] border border-[#3A4640] rounded-md shadow-sm p-5">
      <header className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-[#39FF14]" />
        <h3 className="font-heading font-bold text-[#F8F9FA]">{title}</h3>
        {action && <div className="ml-auto">{action}</div>}
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function KV({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">{label}</div>
      <div className={`text-sm text-[#F8F9FA] ${mono ? "font-mono" : "font-medium"}`}>{value}</div>
    </div>
  );
}

function MetricTile({ label, value, colour, text, accent, testid }) {
  return (
    <div className="rounded-md p-4" style={{ background: colour }} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-heading font-semibold" style={{ color: accent || (colour === "#F5F5F4" ? "#78716c" : "#D97706") }}>{label}</div>
      <div className="font-heading text-xl font-extrabold mt-1" style={{ color: text || (colour === "#F5F5F4" ? "#1c1917" : "#ffffff") }}>{value}</div>
    </div>
  );
}

function RecentTile({ label, value, sub }) {
  return (
    <div className="border border-[#3A4640] rounded p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">{label}</div>
      <div className="text-sm font-heading font-bold text-[#F8F9FA] mt-1">{value}</div>
      <div className="text-xs text-[#8A8F98] mt-0.5 line-clamp-1">{sub}</div>
    </div>
  );
}

function FutureCard({ icon: Icon, title, hint, live, count, liveLabel = "records" }) {
  return (
    <div className="border border-dashed border-[#3A4640] rounded-md p-5 bg-[#0B0F0C]/60">
      <Icon size={18} className="text-[#8A8F98]" />
      <div className="font-heading font-bold text-[#F8F9FA] mt-2">{title}</div>
      {live ? (
        <div className="text-xs text-[#39FF14] font-mono mt-1">{count} {liveLabel}</div>
      ) : (
        <div className="text-xs text-[#8A8F98] mt-1">{hint}</div>
      )}
    </div>
  );
}
